export const currencyFormatter = (value: number | undefined): string => {
  if (typeof value === 'undefined') return ''
  return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const numberFormatter = (value: number | undefined): string => {
  if (typeof value === 'undefined') return ''
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const currencyParser = (displayValue: string | undefined): number => {
  if (!displayValue) return 0
  const parsed = Number.parseFloat(displayValue.replace(/\$\s?|(,*)/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

export const volumeFormatter = (value: number | undefined): string => {
  if (typeof value === 'undefined') return ''

  const str = value.toString()
  const [integerPart, decimalPart] = str.split('.')

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger
}

export const volumeParser = (displayValue: string | undefined): number => {
  if (!displayValue) return 0
  const parsed = Number.parseFloat(displayValue.replace(/,/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

const isIntermediateInputState = (value: string): boolean => {
  // Only preserve truly incomplete input states
  // Allow: "-", ".", "-.", "-0", and trailing decimal like "123."
  return /^-?$|^\.$|^-\.$|^-0$|^-?\d+\.$/.test(value)
}

const isNegativeZeroPattern = (value: string): boolean => {
  // Detect patterns like "-0", "-0.", "-0.0", "-0.00" etc.
  return /^-0\.?0*$/.test(value)
}

const addCommasSeparators = (numberString: string): string => {
  const parts = numberString.split('.')
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts[1] !== undefined ? `${integerPart}.${parts[1]}` : integerPart
}

const enforceDecimalPrecision = (value: number, decimalPlaces: number): string => {
  const fixed = value.toFixed(decimalPlaces)
  // For currency (2 decimals), always show 2 decimals
  if (decimalPlaces === 2) {
    return fixed
  }
  // For other precisions, remove trailing zeros
  return fixed.replace(/\.?0+$/, '')
}

// Precision-safe calculation utilities
export const roundToPrecision = (value: number, decimalPlaces: number): number => {
  const factor = Math.pow(10, decimalPlaces)
  // Use banker's rounding (round half to even) for financial calculations
  const shifted = value * factor
  const rounded = Math.round(shifted)
  return rounded / factor
}

export const multiplyWithPrecision = (a: number | string, b: number | string, precision = 2): number => {
  const numA = typeof a === 'string' ? parseFloat(a) || 0 : Number(a) || 0
  const numB = typeof b === 'string' ? parseFloat(b) || 0 : Number(b) || 0
  const result = numA * numB
  return roundToPrecision(result, precision)
}

export const addWithPrecision = (values: (number | string)[], precision = 2): number => {
  const sum = values.reduce((acc, val) => {
    const num = typeof val === 'string' ? parseFloat(val) || 0 : Number(val) || 0
    return acc + num
  }, 0)
  return roundToPrecision(sum, precision)
}

export const volumeFormatterRobust = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return ''

  const stringValue = String(value)

  // Specific rule for "-0" to always preserve it
  if (stringValue === '-0') {
    return '-0'
  }

  // Convert .0 to 0.0 and -.0 to -0.0
  if (stringValue === '.0') {
    return '0.0'
  }
  if (stringValue === '-.0') {
    return '-0.0'
  }

  if (isIntermediateInputState(stringValue)) {
    return stringValue
  }

  // Handle negative zero patterns specially
  if (isNegativeZeroPattern(stringValue)) {
    return stringValue
  }

  const numericValue = Number(stringValue)
  if (Number.isNaN(numericValue)) {
    return stringValue
  }

  // Preserve negative zero in formatting
  const wasNegativeZero = stringValue.startsWith('-') && numericValue === 0

  const precisionLimited = Math.round(numericValue * 1000) / 1000
  const formattedNumber = enforceDecimalPrecision(precisionLimited, 3)
  const withCommas = addCommasSeparators(formattedNumber)

  // Add negative sign back if it was a negative zero
  return wasNegativeZero && !withCommas.startsWith('-') ? `-${withCommas}` : withCommas
}

export const currencyFormatterRobust = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return ''

  const stringValue = String(value)

  // Specific rule for "-0" to always preserve it
  if (stringValue === '-0') {
    return '-0'
  }

  // Convert .0 to 0.0 and -.0 to -0.0
  if (stringValue === '.0') {
    return '0.0'
  }
  if (stringValue === '-.0') {
    return '-0.0'
  }

  if (isIntermediateInputState(stringValue)) {
    return stringValue
  }

  // Handle negative zero patterns specially
  if (isNegativeZeroPattern(stringValue)) {
    return stringValue
  }

  const numericValue = Number(stringValue)
  if (Number.isNaN(numericValue)) {
    return stringValue
  }

  // Preserve negative zero in formatting
  const wasNegativeZero = stringValue.startsWith('-') && numericValue === 0

  const precisionLimited = Math.round(numericValue * 100) / 100
  const formattedNumber = enforceDecimalPrecision(precisionLimited, 2)
  const withCommas = addCommasSeparators(formattedNumber)

  // Add negative sign back if it was a negative zero
  return wasNegativeZero && !withCommas.startsWith('-') ? `-${withCommas}` : withCommas
}

export const perUnitFormatterRobust = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return ''

  const stringValue = String(value)

  // Specific rule for "-0" to always preserve it
  if (stringValue === '-0') {
    return '-0'
  }

  // Convert .0 to 0.0 and -.0 to -0.0
  if (stringValue === '.0') {
    return '0.0'
  }
  if (stringValue === '-.0') {
    return '-0.0'
  }

  if (isIntermediateInputState(stringValue)) {
    return stringValue
  }

  // Handle negative zero patterns specially
  if (isNegativeZeroPattern(stringValue)) {
    return stringValue
  }

  const numericValue = Number(stringValue)
  if (Number.isNaN(numericValue)) {
    return stringValue
  }

  // Preserve negative zero in formatting
  const wasNegativeZero = stringValue.startsWith('-') && numericValue === 0

  const precisionLimited = Math.round(numericValue * 1000000) / 1000000
  const formattedNumber = enforceDecimalPrecision(precisionLimited, 6)
  const withCommas = addCommasSeparators(formattedNumber)

  // Add negative sign back if it was a negative zero
  return wasNegativeZero && !withCommas.startsWith('-') ? `-${withCommas}` : withCommas
}

export const robustParser = (displayValue: string | undefined): string | number => {
  if (!displayValue || displayValue === '') return ''

  const cleanValue = displayValue.replace(/[\$,\s]/g, '')

  if (
    cleanValue === '' ||
    cleanValue === '-' ||
    cleanValue === '.' ||
    cleanValue === '-0' ||
    cleanValue === '.0' ||
    cleanValue === '-.0'
  ) {
    return cleanValue
  }

  const numericValue = Number(cleanValue)
  return Number.isNaN(numericValue) ? cleanValue : numericValue
}

export const currencyFormatterWithDecimals = (value: string | number | undefined): string => {
  return perUnitFormatterRobust(value)
}

export const currencyParserWithDecimals = (displayValue: string | undefined): string | number => {
  return robustParser(displayValue)
}

export const volumeFormatterThreeDecimals = (value: string | number | undefined): string => {
  return volumeFormatterRobust(value)
}

export const currencyFormatterTwoDecimals = (value: string | number | undefined): string => {
  if (value === undefined || value === null || value === '') return ''

  const stringValue = String(value)

  // Specific rule for "-0" to always preserve it
  if (stringValue === '-0') {
    return '-0.00'
  }

  // Convert .0 to 0.00 and -.0 to -0.00
  if (stringValue === '.0') {
    return '0.00'
  }
  if (stringValue === '-.0') {
    return '-0.00'
  }

  if (isIntermediateInputState(stringValue)) {
    return stringValue
  }

  // Handle negative zero patterns specially
  if (isNegativeZeroPattern(stringValue)) {
    return stringValue
  }

  const numericValue = Number(stringValue)
  if (Number.isNaN(numericValue)) {
    return stringValue
  }

  // Preserve negative zero in formatting
  const wasNegativeZero = stringValue.startsWith('-') && numericValue === 0

  const precisionLimited = Math.round(numericValue * 100) / 100
  // Always format to exactly 2 decimal places for currency
  const formattedNumber = precisionLimited.toFixed(2)
  const withCommas = addCommasSeparators(formattedNumber)

  // Add negative sign back if it was a negative zero
  return wasNegativeZero && !withCommas.startsWith('-') ? `-${withCommas}` : withCommas
}

export const unitPriceFormatter = (value: number | undefined): string => {
  if (typeof value === 'undefined' || value === null) return '0.00'
  if (value === 0) return '0.00'

  const fixedValue = Number(value).toFixed(6)
  const [integerPart, decimalPart = ''] = fixedValue.split('.')

  // Remove trailing zeros from decimal part, but keep at least 2 decimal places
  const trimmedDecimalPart = decimalPart.replace(/0+$/, '')
  const finalDecimalPart = trimmedDecimalPart.length < 2 ? trimmedDecimalPart.padEnd(2, '0') : trimmedDecimalPart

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return finalDecimalPart ? `${formattedInteger}.${finalDecimalPart}` : formattedInteger
}
